import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppointmentCalendar, { type CalendarAppointment } from '../AppointmentCalendar';
import { fetchInstallationAppointments } from '../../api/installationAppointments';
import { fetchSiteInspections } from '../../api/siteInspectionAppointments';
import { fetchOnsiteServiceAppointments } from '../../api/onsiteServiceAppointments';

const INSPECTION_COLOR = '#7c3aed';
const INSTALLATION_COLOR = '#2f6f44';
const ONSITE_SERVICE_COLOR = '#b45309';

const InstallationCalendarTab: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalendarAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    Promise.all([fetchSiteInspections(), fetchInstallationAppointments(), fetchOnsiteServiceAppointments()])
      .then(([inspections, installations, onsite]) => {
        if (cancelled) return;
        const next: CalendarAppointment[] = [];
        for (const r of [...inspections.scheduled, ...inspections.completed]) {
          if (!r.inspection_at) continue;
          next.push({
            id: `inspection-${r.id}`,
            date: r.inspection_at,
            endDate: r.inspection_end_at,
            title: r.business_name || 'Site inspection',
            color: INSPECTION_COLOR,
            subtitle: `Site inspection · ${r.status}`,
            onClick: () => navigate(`/installation/site-inspection/${r.id}`),
          });
        }
        for (const r of [...installations.scheduled, ...installations.completed]) {
          if (!r.installation_at) continue;
          next.push({
            id: `installation-${r.id}`,
            date: r.installation_at,
            endDate: r.installation_end_at,
            title: r.business_name || 'Installation',
            color: INSTALLATION_COLOR,
            subtitle: `Installation · ${r.status}`,
            onClick: () => navigate(`/installation/${r.id}`),
          });
        }
        for (const r of [...onsite.scheduled, ...onsite.completed]) {
          if (!r.service_at) continue;
          next.push({
            id: `onsite-${r.id}`,
            date: r.service_at,
            endDate: r.service_end_at,
            title: r.business_name || 'Onsite service',
            color: ONSITE_SERVICE_COLOR,
            subtitle: `Onsite service · ${r.status}`,
            onClick: () => navigate(`/installation/onsite-service/${r.id}`),
          });
        }
        setEvents(next);
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Failed to load appointments.');
          setEvents([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const legend = useMemo(
    () => [
      { label: 'Site inspection', color: INSPECTION_COLOR },
      { label: 'Installation & deployment', color: INSTALLATION_COLOR },
      { label: 'Onsite service', color: ONSITE_SERVICE_COLOR },
    ],
    []
  );

  if (loadError) {
    return (
      <p className="create-order-error" role="alert">
        {loadError}
      </p>
    );
  }
  if (loading) {
    return <p className="page-subtitle">Loading appointments…</p>;
  }

  return (
    <AppointmentCalendar
      events={events}
      legend={legend}
      hint="Scheduled and completed site inspections, installations and onsite service visits. Appointments that span a date range are shown on every day they cover. Click an appointment to open it."
    />
  );
};

export default InstallationCalendarTab;
